import React, { useEffect, useState } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
  PDFViewer
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import sanitizeHtml from 'sanitize-html';
import { Parser } from 'htmlparser2';
import { Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import Compressor from 'compressorjs';
import { API_URL } from '..';

// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  section: {
    marginBottom: 20,
  },
  image: {
    width: 200,
    maxHeight: 200,
    objectFit: 'contain',
    marginBottom: 5,
    
  },
  imageContainer: {
    position: 'relative',
    marginBottom: 10,
    padding: 5,
   
  },
  imageStatus: {
    position: 'absolute',
    top: 10,
    left: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    padding: '2px 5px',
    fontSize: 10,
    textTransform: 'uppercase',
  },
  descriptionParagraph: {
    marginBottom: 0,
    fontSize: 8,
    lineHeight: 1.2,
    marginRight:5,
  },
  descriptionBold: {
    fontWeight: 700,
    fontSize: 8,
    lineHeight: 1.2,
    marginRight:5,
  },
  listItem: {
    marginLeft: 5,
    marginBottom: 3,
    fontSize: 8,
    marginRight:5,
  },
  
});

// Function to compress image and convert to base64
const toBase64ForFooter = async (url) => {
  try {
    const response = await fetch(`${API_URL}/proxy-image?url=${encodeURIComponent(url)}`, {
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', url, error);
    return url;
  }
};
const toBase64 = async (url) => {
  try {
    
    const response = await fetch(
      `${API_URL}/proxy-image?url=${encodeURIComponent(url)}`,
      { mode: 'cors' }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      new Compressor(blob, {
        quality:1,
        maxWidth: 800,
        maxHeight: 800,
        mimeType: 'image/jpeg',
        success(compressedBlob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result;
            
            resolve(base64String);
          };
          reader.onerror = () => {
            
            reject(new Error('Failed to read compressed image'));
          };
          reader.readAsDataURL(compressedBlob);
        },
        error(err) {
          
          reject(new Error('Image compression failed'));
        },
      });
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', url, error);
    return null;
  }
};

// Parse HTML to PDF components (unchanged)
const parseHtmlToPdf = (html) => {
  const elements = [];
  let currentText = '';
  let currentStyles = {};
  let isInsideList = false;
  let isInsideListItem = false;
  let listItemIndex = 0;

  const parser = new Parser(
    {
      onopentag(name, attribs) {
        if (name === 'p') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={styles.descriptionParagraph}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          currentStyles = { ...styles.descriptionParagraph };
        } else if (name === 'strong') {
          currentStyles = { ...currentStyles, ...styles.descriptionBold };
        } else if (name === 'br') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          elements.push(
            <Text key={elements.length} style={currentStyles}>
              {'\n'}
            </Text>
          );
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = true;
          listItemIndex = 0;
        } else if (name === 'li') {
          isInsideListItem = true;
          listItemIndex += 1;
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          currentStyles = { ...styles.descriptionParagraph, ...styles.listItem };
          if (attribs['data-list'] === 'ordered') {
            currentText = `${listItemIndex}. `;
          } else if (attribs['data-list'] === 'bullet') {
            currentText = '• ';
          } else {
            currentText = '• ';
          }
        }
      },
      ontext(text) {
        currentText += text.trim();
      },
      onclosetag(name) {
        if (name === 'p' || name === 'strong' || name === 'li') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          if (name === 'strong') {
            currentStyles = { ...styles.descriptionParagraph };
          } else if (name === 'li') {
            isInsideListItem = false;
            currentStyles = { ...styles.descriptionParagraph };
          }
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = false;
          listItemIndex = 0;
        }
      },
    },
    { decodeEntities: true }
  );

  parser.write(
    sanitizeHtml(html, {
      allowedTags: ['p', 'strong', 'br', 'ul', 'ol', 'li'],
      allowedAttributes: { li: ['data-list'] },
    })
  );
  parser.end();

  return elements.length > 0 ? (
    <View>{elements}</View>
  ) : (
    <Text style={styles.descriptionParagraph}>N/A</Text>
  );
};

// Parse HTML to HTML elements (unchanged)
const parseHtmlToHtml = (html) => {
  const elements = [];
  let currentText = '';
  let isInsideList = false;
  let listItemIndex = 0;

  const parser = new Parser(
    {
      onopentag(name, attribs) {
        if (name === 'p') {
          if (currentText) {
            elements.push(
              `<p style="margin-bottom: 0; font-size: 10px; line-height: 1.8;">${currentText}</p>`
            );
            currentText = '';
          }
        } else if (name === 'strong') {
          if (currentText) {
            elements.push(
              `<p style="font-weight: 700; font-size: 10px; line-height: 1.8;">${currentText}</p>`
            );
            currentText = '';
          }
        } else if (name === 'br') {
          if (currentText) {
            elements.push(
              `<p style="margin-bottom: 0; font-size: 10px; line-height: 1.8;">${currentText}</p>`
            );
            currentText = '';
          }
          elements.push('<br>');
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = true;
          listItemIndex = 0;
          elements.push(
            `<${name} style="margin-left: 5px; list-style-type: ${
              name === 'ol' ? 'decimal' : 'disc'
            };">`
          );
        } else if (name === 'li') {
          listItemIndex += 1;
          if (currentText) {
            elements.push(
              `<li style="margin-left: 5px; margin-bottom: 3px; font-size: 10px;">${
                attribs['data-list'] === 'ordered' ? `${listItemIndex}. ` : '• '
              }${currentText}</li>`
            );
            currentText = '';
          }
        }
      },
      ontext(text) {
        currentText += text.trim();
      },
      onclosetag(name) {
        if (name === 'p' || name === 'strong') {
          if (currentText) {
            elements.push(
              `<p style="${
                name === 'strong'
                  ? 'font-weight: 700; font-size: 10px; line-height: 1.8;'
                  : 'margin-bottom: 0; font-size: 10px; line-height: 1.8;'
              }">${currentText}</p>`
            );
            currentText = '';
          }
        } else if (name === 'li') {
          if (currentText) {
            elements.push(
              `<li style="margin-left: 5px; margin-bottom: 3px; font-size: 10px;">${
                isInsideList
                  ? elements[elements.length - 1].includes('<ol')
                    ? `${listItemIndex}. `
                    : '• '
                  : ''
              }${currentText}</li>`
            );
            currentText = '';
          }
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = false;
          listItemIndex = 0;
          elements.push(`</${name}>`);
        }
      },
    },
    { decodeEntities: true }
  );

  parser.write(
    sanitizeHtml(html, {
      allowedTags: ['p', 'strong', 'br', 'ul', 'ol', 'li'],
      allowedAttributes: { li: ['data-list'] },
    })
  );
  parser.end();

  return elements.length > 0
    ? elements.join('')
    : '<p style="margin-bottom: 0; font-size: 10px; line-height: 1.8;">N/A</p>';
};
function isHTMLString(str) {
  // Check if str is a string and not empty
  if (typeof str !== 'string' || str.trim() === '') {
    return false;
  }
  
  // Regular expression to detect HTML tags
  const htmlRegex = /<[a-z][\s\S]*>/i;
  
  // Test the string for HTML tags
  return htmlRegex.test(str);
}
// Format date function (unchanged)
const formatDate = (input) => {
  const date = new Date(input);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}.${month}.${year}`;
};

const WorkCompletionPDF = ({ initialData }) => {
  const isSignature = initialData?.clientSignature ? initialData?.clientSignature : null
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section} fixed>
          <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <Image source="assets/images/logo.png" style={{ width: 100 }} />
            <Image
              source={toBase64(
                "https://api.test.frontforcecrm.com/logo/update.jpeg"
              )}
              style={{ width: 100 }}
            />
          </View>
        </View>
        <View>
          <Text
            style={{
              textAlign: "center",
              fontWeight: "bold",
              textDecoration: "underline",
              fontSize: 12,
            }}
          >
            Work Completion Report
          </Text>
        </View>
        <View style={{ marginTop: 20 }}>
          <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderTopWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
            }}
          >
            <Text style={{ fontSize: 10, marginTop: 5, fontWeight: "bold" }}>
              Client Details
            </Text>
          </View>
          <View
            style={{
              backgroundColor: "#fff",
              flexDirection: "row",
              paddingHorizontal: 5,
              borderWidth: 1,
            }}
          >
            <View style={{ width: "20%" }}>
              <Text
                style={{
                  textDecoration: "underline",
                  fontSize: 8,
                  padding: 10,
                  fontWeight: "bold",
                }}
              >
                Name
              </Text>
            </View>
            <View style={{ borderRightWidth: 1 }} />
            <View style={{ justifyContent: "center", width: "70%" }}>
              <Text style={{ fontSize: 8, padding: 10 }}>
                {initialData.client_name || "John Doe"}
              </Text>
            </View>
          </View>
          <View
            style={{
              backgroundColor: "#fff",
              flexDirection: "row",
              paddingHorizontal: 5,
              borderBottomWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
            }}
          >
            <View style={{ width: "20%" }}>
              <Text
                style={{
                  textDecoration: "underline",
                  fontSize: 8,
                  padding: 10,
                  fontWeight: "bold",
                  marginTop: 20,
                }}
              >
                Address
              </Text>
            </View>
            <View style={{ borderRightWidth: 1 }} />
            <View style={{ justifyContent: "center", width: "50%" }}>
              <Text style={{ fontSize: 8, padding: 10 }}>
                {initialData?.customer?.address || "Client Address"}
              </Text>
            </View>
            <View
              style={{
                justifyContent: "center",
                width: "30%",
                borderLeftWidth: 1,
              }}
            >
              <View>
                <Text
                  style={{
                    fontSize: 8,
                    padding: 5,
                    marginTop: 10,
                    fontWeight: "bold",
                  }}
                >
                  Postcode
                </Text>
                <View style={{ borderWidth: 0.5 }} />
                <Text style={{ fontSize: 8, padding: 10 }}>
                  {initialData?.customer?.postcode}
                </Text>
              </View>
            </View>
          </View>
          <View
            style={{
              marginTop: 20,
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderTopWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
            }}
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Site Details
            </Text>
          </View>

          <View
            style={{
              backgroundColor: "#fff",
              flexDirection: "row",
              paddingHorizontal: 5,
              borderBottomWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
            }}
          >
            <View style={{ width: "20%" }}>
              <Text
                style={{
                  textDecoration: "underline",
                  fontSize: 8,
                  padding: 10,
                  fontWeight: "bold",
                  marginTop: 20,
                }}
              >
                Address
              </Text>
            </View>
            <View style={{ borderRightWidth: 1 }} />
            <View style={{ justifyContent: "center", width: "50%" }}>
              <Text style={{ fontSize: 8, padding: 10 }}>
                {initialData?.address || "Client Address"}
              </Text>
            </View>
            <View
              style={{
                justifyContent: "center",
                width: "30%",
                borderLeftWidth: 1,
              }}
            >
              <View>
                <Text
                  style={{
                    fontSize: 8,
                    padding: 5,
                    marginTop: 10,
                    fontWeight: "bold",
                  }}
                >
                  Postcode
                </Text>
                <View style={{ borderWidth: 0.5 }} />
                <Text style={{ fontSize: 8, padding: 10 }}>
                  {initialData?.postcode}
                </Text>
              </View>
            </View>
          </View>
          <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderWidth: 1,
              marginTop: 10,
            }}
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Job Details
            </Text>
          </View>
          <View
            style={{
              backgroundColor: "#fff",
              flexDirection: "row",
              paddingHorizontal: 5,
              borderBottomWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
            }}
          >
            <View style={{ justifyContent: "center", width: "50%" }}>
              <Text style={{ fontSize: 8, padding: 5 }}>
                <Text style={{ fontWeight: "bold", fontSize: 8 }}>
                  Reference No{" "}
                </Text>
                : {initialData?.referenceNo}
              </Text>
            </View>
            <View
              style={{
                justifyContent: "center",
                width: "50%",
                borderLeftWidth: 1,
              }}
            >
              <Text style={{ fontSize: 8, padding: 5 }}>
                <Text style={{ fontWeight: "bold", fontSize: 8 }}>
                  Order No{" "}
                </Text>
                : #{initialData?.id}
              </Text>
            </View>
            <View
              style={{
                justifyContent: "center",
                width: "50%",
                borderLeftWidth: 1,
              }}
            >
              <View>
                <Text style={{ fontSize: 8, padding: 5 }}>
                  <Text style={{ fontWeight: "bold", fontSize: 8 }}>
                    Date{" "}
                  </Text>
                  : {formatDate(initialData?.createdAt)}
                </Text>
              </View>
            </View>
          </View>
          <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderWidth: 1,
              marginTop: 10,
            }}
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Job Description
            </Text>
          </View>
          <View
            style={{
              borderBottomWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
              padding: 5,
            }}
          >
            {parseHtmlToPdf(initialData?.message)}
          </View>
          <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderWidth: 1,
              marginTop: 10,
            }}
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Job Checklist
            </Text>
          </View>

          <View style={{borderWidth:1}}>
            <View style={{flexDirection:'row', columnGap: 50}}>
              <View style={{borderRightWidth:1,width:'50%'}}>
                <Text style={{ fontSize: 8,paddingLeft: 10,paddingTop:5,fontWeight:'bold'}} >Functional Testing</Text>
              </View>
              <View style={{flexDirection:'row',width:'50%'}}>
              <View style={{flexDirection:'row',justifyContent:'center', paddingRight: 10}}>
                       <View>

                      {
                          initialData?.functionalTesting === true ? 
                          <Image
                          source={toBase64(
                            "https://api.test.frontforcecrm.com/logo/check-mark.png"
                          )}
                          style={{ width: 15,marginTop:4 }}
                        />:
                          <Text style={{borderWidth:1,padding:6,marginTop:4}}></Text>
                        }
                        </View>
                        <View style={{justifyContent:'center',marginTop:5,marginLeft:5}}>

                       <Text  style={{ fontSize: 8}}>Yes</Text>
                        </View>
                 </View>
                 <View style={{flexDirection:'row',justifyContent:'center'}}>
                       <View>

                      {
                          initialData?.functionalTesting === false ? 
                          <Image
                          source={toBase64(
                            "https://api.test.frontforcecrm.com/logo/check-mark.png"
                          )}
                          style={{ width: 15,marginTop:4 }}
                        />:
                          <Text style={{borderWidth:1,padding:6,marginTop:4}}></Text>
                        }
                        </View>
                        <View style={{justifyContent:'center',marginTop:5,marginLeft:5}}>

                       <Text  style={{ fontSize: 8}}>No</Text>
                        </View>
                 </View>
              </View>
            </View>
            <View style={{borderWidth: 0.4}}/>
            <View style={{flexDirection:'row', columnGap: 50}}>
            <View style={{borderRightWidth:1,width:'50%'}}>
                <Text style={{ fontSize: 8,paddingLeft: 10,paddingTop:5,fontWeight:'bold'}} >Electrician Certificate Issue</Text>
              </View>
              <View style={{flexDirection:'row',width:'50%'}}>
              <View style={{flexDirection:'row',justifyContent:'center',paddingRight: 10}}>
                       <View>

                      {
                          initialData?.certificateIssue === true ? 
                          <Image
                          source={toBase64(
                            "https://api.test.frontforcecrm.com/logo/check-mark.png"
                          )}
                          style={{ width: 15,marginTop:4 }}
                        />:
                          <Text style={{borderWidth:1,padding:6,marginTop:4}}></Text>
                        }
                        </View>
                        <View style={{justifyContent:'center',marginTop:5,marginLeft:5}}>

                       <Text  style={{ fontSize: 8}}>Yes</Text>
                        </View>
                 </View>
                 <View style={{flexDirection:'row',justifyContent:'center'}}>
                       <View>

                      {
                          initialData?.certificateIssue === false ? 
                          <Image
                          source={toBase64(
                            "https://api.test.frontforcecrm.com/logo/check-mark.png"
                          )}
                          style={{ width: 15,marginTop:4 }}
                        />:
                          <Text style={{borderWidth:1,padding:6,marginTop:4}}></Text>
                        }
                        </View>
                        <View style={{justifyContent:'center',marginTop:5,marginLeft:5}}>

                       <Text  style={{ fontSize: 8}}>No</Text>
                        </View>
                 </View>
              </View>
            </View>
            <View style={{borderWidth: 0.4}}/>
            <View style={{flexDirection:'row', columnGap: 50}}>
            <View style={{borderRightWidth:1,width:'50%'}}>
                <Text style={{ fontSize: 8,paddingLeft: 10,paddingTop:5,fontWeight:'bold'}} >Further Information</Text>
              </View>
              <View style={{width:'50%',paddingRight:5}}>
               {isHTMLString(initialData?.futherDescription) === true ? parseHtmlToPdf(initialData?.futherDescription) : <Text style={{fontSize: 8,lineHeight:1.2}}>{initialData?.futherDescription}</Text> }
              </View>
            </View>
          </View>
          <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderTopWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
              marginTop: 10,
            }}
            break
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Media/Photo Evidence
            </Text>
          </View>
          <View>
            {initialData?.images?.length === 0 ? (
              <Text style={{ fontSize: 8, paddingTop: 10 }}>
                No image found...
              </Text>
            ) : null}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            {initialData?.images?.slice(0, 2).map((e, index) => (
              <View key={index} style={{ width: "50%", marginTop: 20 }}>
                <Image
                  style={{ marginTop:10,width: 160, height: 160 , objectFit: 'contain'}}
                  src={toBase64(API_URL + e.image_url)}
                />
               
                <Text style={{ width: 200 ,fontSize: 8,marginTop:10,marginBottom: 30,lineHeight: 1.5}}>
                  {e.description || ""}
                 
                </Text>
              </View>
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            {initialData?.images?.slice(2, 4).map((e, index) => (
              <View key={index} style={{ width: "50%", marginTop: 10 }}>
                <Image
                  style={{ marginTop:10,width: 160, height: 160 , objectFit: 'contain'}}
                  src={toBase64(API_URL + e.image_url)}
                />
                <Text style={{ width: 200 ,fontSize: 8,marginTop:10,marginBottom: 30,lineHeight: 1.5}}>
                  {e.description || ""}
              
                </Text>
              </View>
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}  break={initialData.images?.length > 4 ? true:false} >
            {initialData?.images?.slice(4, 6).map((e, index) => (
              <View key={index} style={{ width: "50%", marginTop: 20 }}>
                <Image
                  style={{ marginTop:10,width: 160, height: 160 , objectFit: 'contain'}}
                  src={toBase64(API_URL + e.image_url)}
                />
                <Text style={{ width: 200 ,fontSize: 8,marginTop:10,marginBottom: 30,lineHeight: 1.5}}>
                  {e.description || ""}
                 
                </Text>
              </View>
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }} >
            {initialData?.images?.slice(6, 8).map((e, index) => (
              <View key={index} style={{ width: "50%", marginTop: 10 }}>
                <Image
                  style={{ marginTop:10,width: 160, height: 160 , objectFit: 'contain'}}
                  src={toBase64(API_URL + e.image_url)}
                />
                <Text style={{ width: 200 ,fontSize: 8,marginTop:10,lineHeight: 1.5,marginBottom: 30}}>
                  {e.description || ""}
                
                </Text>
              </View>
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}  break={initialData.images?.length > 8 ? true:false}>
            {initialData?.images?.slice(8, 10).map((e, index) => (
              <View key={index} style={{ width: "50%", marginTop: 20 }}>
                <Image
                  style={{ marginTop:10,width: 160, height: 160 , objectFit: 'contain'}}
                  src={toBase64(API_URL + e.image_url)}
                />
                <Text style={{ width: 200 ,fontSize: 8,marginTop:10,lineHeight: 1.5,marginBottom: 30}}>
                  {e.description || ""}
                 
                </Text>
              </View>
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }} >
            {initialData?.images?.slice(10, 12).map((e, index) => (
              <View key={index} style={{ width: "50%", marginTop: 20 }}>
                <Image
                  style={{ width: 160, height: 160 , objectFit: 'contain'}}
                  src={toBase64(API_URL + e.image_url)}
                />
                <Text style={{ width: 200 ,fontSize: 8,marginTop:10,lineHeight: 1.5,marginBottom: 30}}>
                  {e.description || ""}
              
                </Text>
              </View>
            ))}
          </View>
        
         
          {
            isSignature ===null ? null : (
              <>
               <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderTopWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
              marginTop: 10,
            }}
            
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Signed By <Text style={{fontSize: 6}}>(On-Site Representative)</Text>
            </Text>
            
          </View>
                <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <View style={{ marginTop: 10 }}>
              
             <Image style={{width: 80,height: 80}} source={toBase64(String(isSignature))}/>
             {
              initialData?.signatoryName && 
             <Text style={{fontSize: 8}}>{initialData?.signatoryName}</Text>
             }
            </View>
            
          </View>
              </>
            )
          }
            <View
            style={{
              backgroundColor: "#ed7d32",
              paddingVertical: 1,
              paddingHorizontal: 5,
              borderTopWidth: 1,
              borderRightWidth: 1,
              borderLeftWidth: 1,
              marginTop: 10,
            }}
            break={initialData.images?.length > 8 ? true:false}
          >
            <Text style={{ fontSize: 8, marginTop: 5, fontWeight: "bold" }}>
              Trading Title
            </Text>
          </View>
          <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <View style={{ marginTop: 10 }}>
              <Text style={{ fontSize: 8 }}>R-wire Limited</Text>
              <Text style={{ fontSize: 8 }}>
                1 Hardy Road {"\n"}London, E4 8YU
              </Text>
            </View>
            <View style={{ marginTop: 20 }}>
              <Text style={{ fontWeight: "bold", fontSize: 8 }}>
                Phone (Mob.): 07950861019
              </Text>
              <Text style={{ fontWeight: "bold", fontSize: 8 }}>
                NICEIC Enrolment Number: 605485000
              </Text>
            </View>
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "center",
              columnGap: 8,
              borderTop: 1,
              borderColor: "#c0c0c0",
              paddingTop: 15,
              marginTop: 40,
            }}
            fixed
          >
            <Image
              source={toBase64ForFooter(
                "https://api.test.frontforcecrm.com/logo/icos.png"
              )}
              
              style={{ objectFit: "contain" ,width: 450}}
            />
          </View>
          
        </View>
      </Page>
    </Document>
  );
};
// ReportPDF component with HTML preview and download button
const ReportPDF = ({ initialData }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageBase64, setImageBase64] = useState({});

  useEffect(() => {
    const loadImages = async () => {
      const base64Images = {};
      const maxImages = 12;
      const imagesToProcess = initialData?.images?.slice(0, maxImages) || [];

      // Load job images
      for (const [index, image] of imagesToProcess.entries()) {
        const base64 = await toBase64(`${API_URL}${image.image_url}`);
        base64Images[index] = base64;
      }
      // Load check mark image
      base64Images['check'] = await toBase64(
        'https://api.test.frontforcecrm.com/logo/check-mark.png'
      );
      // Load footer logo
      base64Images['footer'] = await toBase64(
        'https://api.test.frontforcecrm.com/logo/icos.png'
      );
      // Load secondary logo
      base64Images['secondary'] = await toBase64(
        'https://api.test.frontforcecrm.com/logo/update.jpeg'
      );
      
      setImageBase64(base64Images);
      setIsLoading(false);
    };
    loadImages();
  }, [initialData]);

  const downloadPDF = async () => {
    setIsDownloading(true);
    try {
      const blob = await pdf(
        <WorkCompletionPDF initialData={initialData} imageBase64={imageBase64} />
      ).toBlob();
      saveAs(blob, `#${initialData?.id}_Work_Completion_Report.pdf`);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
      }}
    >
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spinner color="#ed7d32" loading={isLoading} size="lg" />
          <p style={{ marginTop: '16px', color: '#4b5563' }}>
            Generating Preview...
          </p>
        </div>
      ) : (
        <>
        {/* <PDFViewer
        style={{width: '100%',height:600}}
        >
          <WorkCompletionPDF initialData={initialData}/>

        </PDFViewer> */}

       
          <div
            style={{
              width: '795px',
              minHeight: '842px',
              padding: '30px',
              backgroundColor: '#fff',
              boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              fontFamily: 'Helvetica, sans-serif',
              fontSize: '12px',
              lineHeight: '1.5',
            }}
          >
            {/* Header Section */}
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <img
                src="assets/images/logo.png"
                alt="Logo"
                style={{ width: '100px' }}
              />
              <img
                src={
                  imageBase64['secondary'] ||
                  'https://api.test.frontforcecrm.com/logo/update.jpeg'
                }
                alt="Secondary Logo"
                style={{ width: '100px' }}
              />
            </div>
            {/* Title */}
            <div style={{ textAlign: 'center' }}>
              <p
                style={{
                  fontSize: '18px',
                  color: 'black',
                  marginTop: '5px',
                  marginBottom: 0,
                  fontWeight: 'bold',
                }}
              >
                Work Completion Report
              </p>
            </div>
            {/* Client Details */}
            <div style={{ marginTop: '20px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  borderTop: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Client Details
                </p>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  display: 'flex',
                  border: '1px solid black',
                }}
              >
                <div style={{ width: '20%' }}>
                  <p
                    style={{
                      textDecoration: 'underline',
                      fontSize: '10px',
                      padding: '10px',
                      fontWeight: 'bold',
                    }}
                  >
                    Name
                  </p>
                </div>
                <div style={{ borderRight: '1px solid black' }}></div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '70%',
                  }}
                >
                  <p style={{ fontSize: '10px', padding: '10px' }}>
                    {initialData.client_name || 'John Doe'}
                  </p>
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  display: 'flex',
                  borderBottom: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <div style={{ width: '20%' }}>
                  <p
                    style={{
                      textDecoration: 'underline',
                      fontSize: '10px',
                      padding: '10px',
                      fontWeight: 'bold',
                      marginTop: '20px',
                    }}
                  >
                    Address
                  </p>
                </div>
                <div style={{ borderRight: '1px solid black' }}></div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '60%',
                  }}
                >
                  <p style={{ fontSize: '10px', padding: '10px' }}>
                    {initialData?.customer?.address || 'Client Address'}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: '1px solid black',
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: '10px',
                        padding: '5px',
                        marginTop: '10px',
                        fontWeight: 'bold',
                      }}
                    >
                      Postcode
                    </p>
                    <div style={{ border: '0.5px solid black' }}></div>
                    <p style={{ fontSize: '10px', padding: '10px' }}>
                      {initialData?.customer?.postcode}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Site Details */}
            <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  border: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Site Details
                </p>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  display: 'flex',
                  borderBottom: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <div style={{ width: '20%' }}>
                  <p
                    style={{
                      textDecoration: 'underline',
                      fontSize: '10px',
                      padding: '10px',
                      fontWeight: 'bold',
                      marginTop: '20px',
                    }}
                  >
                    Address
                  </p>
                </div>
                <div style={{ borderRight: '1px solid black' }}></div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '60%',
                  }}
                >
                  <p style={{ fontSize: '10px', padding: '10px' }}>
                    {initialData?.address || 'Client Address'}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: '1px solid black',
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: '10px',
                        padding: '5px',
                        marginTop: '10px',
                        fontWeight: 'bold',
                      }}
                    >
                      Postcode
                    </p>
                    <div style={{ border: '0.5px solid black' }}></div>
                    <p style={{ fontSize: '10px', padding: '10px' }}>
                      {initialData?.postcode}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Job Details */}
            <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  border: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Job Details
                </p>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  display: 'flex',
                  borderBottom: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '50%',
                  }}
                >
                  <p style={{ fontSize: '10px', padding: '5px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '10px' }}>
                      Reference No{' '}
                    </span>
                    : {initialData?.referenceNo}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    borderLeft: '1px solid black',
                    alignItems: 'center',
                    width: '50%',
                  }}
                >
                  <p style={{ fontSize: '10px', padding: '5px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '10px' }}>
                      Order No{' '}
                    </span>
                    : #{initialData?.id}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '50%',
                    borderLeft: '1px solid black',
                  }}
                >
                  <div>
                    <p style={{ fontSize: '10px', padding: '5px' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '10px' }}>
                        Date{' '}
                      </span>
                      : {formatDate(initialData?.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Job Description */}
            <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  border: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Job Description
                </p>
              </div>
              <div
                style={{
                  borderBottom: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                  padding: '5px',
                }}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: parseHtmlToHtml(initialData?.message),
                  }}
                />
              </div>
            </div>
            {/* Job Checklist */}
            <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  border: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Job Checklist
                </p>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div style={{}}>
                    <p
                      style={{
                        textDecoration: 'underline',
                        fontSize: '10px',
                        padding: '10px',
                        fontWeight: 'bold',
                        marginTop: '0px',
                      }}
                    >
                      Functional Testing
                    </p>
                  </div>
                  <div style={{ display: 'flex', marginLeft: 80 }}>
                    <div style={{ display: 'flex' }}>
                      <span style={{ marginTop: 5 }}>
                        {initialData?.functionalTesting === true ? (
                          <Icon
                            icon="fa-solid:check"
                            width="14"
                            height="14"
                            style={{ borderWidth: 1 }}
                          />
                        ) : (
                          <span style={{ borderWidth: 1, padding: 6, marginTop: 4 }}></span>
                        )}
                      </span>
                      <p
                        style={{
                          textDecoration: 'underline',
                          fontSize: '10px',
                          padding: '10px',
                          fontWeight: 'bold',
                          marginTop: '0px',
                          display: 'flex',
                        }}
                      >
                        Yes
                      </p>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ marginTop: 5 }}>
                        {initialData?.functionalTesting === false ? (
                          <Icon
                            icon="fa-solid:check"
                            width="14"
                            height="14"
                            style={{ borderWidth: 1 }}
                          />
                        ) : (
                          <span style={{ borderWidth: 1, padding: 6, marginTop: 4 }}></span>
                        )}
                      </span>
                      <p
                        style={{
                          textDecoration: 'underline',
                          fontSize: '10px',
                          padding: '10px',
                          fontWeight: 'bold',
                          marginTop: '0px',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        No
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  borderTop: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div style={{}}>
                    <p
                      style={{
                        textDecoration: 'underline',
                        fontSize: '10px',
                        padding: '10px',
                        fontWeight: 'bold',
                        marginTop: '0px',
                      }}
                    >
                      Electrician Certificate Issue
                    </p>
                  </div>
                  <div style={{ display: 'flex', marginLeft: 80 }}>
                    <div style={{ display: 'flex' }}>
                      <span style={{ marginTop: 5 }}>
                        {initialData?.certificateIssue === true ? (
                          <Icon
                            icon="fa-solid:check"
                            width="14"
                            height="14"
                            style={{ borderWidth: 1 }}
                          />
                        ) : (
                          <span style={{ borderWidth: 1, padding: 6, marginTop: 4 }}></span>
                        )}
                      </span>
                      <p
                        style={{
                          textDecoration: 'underline',
                          fontSize: '10px',
                          padding: '10px',
                          fontWeight: 'bold',
                          marginTop: '0px',
                          display: 'flex',
                        }}
                      >
                        Yes
                      </p>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <span style={{ marginTop: 5 }}>
                        {initialData?.certificateIssue === false ? (
                          <Icon
                            icon="fa-solid:check"
                            width="14"
                            height="14"
                            style={{ borderWidth: 1 }}
                          />
                        ) : (
                          <span style={{ borderWidth: 1, padding: 6, marginTop: 4 }}></span>
                        )}
                      </span>
                      <p
                        style={{
                          textDecoration: 'underline',
                          fontSize: '10px',
                          padding: '10px',
                          fontWeight: 'bold',
                          marginTop: '0px',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        No
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#fff',
                  borderTop: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                  borderBottom: '1px solid black',
                }}
              >
                {initialData?.futherDescription && (
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <div style={{}}>
                      <p
                        style={{
                          textDecoration: 'underline',
                          fontSize: '10px',
                          padding: '10px',
                          fontWeight: 'bold',
                          marginTop: '0px',
                        }}
                      >
                        Further Information
                      </p>
                    </div>
                    <div style={{ display: 'flex', marginLeft: 80 }}>
                      
                      <div
                        dangerouslySetInnerHTML={{
                          __html: initialData.futherDescription,
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Media/Photo Evidence */}
            <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  borderTop: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Media/Photo Evidence
                </p>
              </div>
              <div>
                {initialData?.images?.length === 0 ? (
                  <p style={{ fontSize: '10px', paddingTop: '10px' }}>
                    No image found...
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(initialData?.images?.slice(0, 12) || []).map((e, index) => (
                      <div key={index} style={{ width: '50%', marginTop: '10px' }}>
                        <div style={{ position: 'relative' }}>
                          {imageBase64[index] ? (
                            <>
                              <img
                                style={{ width: '200px', maxHeight: '200px', objectFit: 'contain' }}
                                src={imageBase64[index]}
                                alt={`Evidence ${index + 1}`}
                              />
                             
                            </>
                          ) : (
                            <p style={{ fontSize: '10px', color: 'red' }}>
                              Failed to load image {index + 1}
                            </p>
                          )}
                        </div>
                        <p style={{ width: '200px', fontSize: '10px', }}>
                          {e.description || ''}{' '}
                         
                        </p>
                      </div>
                    ))}
                    {initialData?.images?.length > 12 && (
                      <p style={{ fontSize: '10px', color: 'red', marginTop: '10px' }}>
                        Note: Only the first 12 images are displayed. Additional images are available in the original data.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Trading Title */}
         
            {
              initialData.clientSignature && (

            <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  borderTop: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Signed By <small>(On-Site Representative)</small>
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginTop: '10px' }}>
                  
                  <img src={initialData.clientSignature} style={{width: 80,height:80}}/>
                  {initialData.signatoryName && 
                  <p style={{textAlign:'center'}}>{initialData.signatoryName || null}</p>
                  }
                </div>
              
              </div>
            </div>
              )
            }
               <div style={{ marginTop: '10px' }}>
              <div
                style={{
                  backgroundColor: '#ed7d32',
                  padding: '1px 5px',
                  borderTop: '1px solid black',
                  borderRight: '1px solid black',
                  borderLeft: '1px solid black',
                }}
              >
                <p
                  style={{
                    fontSize: '16px',
                    color: 'black',
                    marginTop: '5px',
                    marginBottom: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Trading Title
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginTop: '10px' }}>
                  <p style={{ fontSize: '10px' }}>R-wire Limited</p>
                  <p style={{ fontSize: '10px' }}>
                    1 Hardy Road
                    <br />
                    London, E4 8YU
                  </p>
                </div>
                <div style={{ marginTop: '20px' }}>
                  <p style={{ fontWeight: 'bold', fontSize: '10px' }}>
                    Phone (Mob.): 07950861019
                  </p>
                  <p style={{ fontWeight: 'bold', fontSize: '10px' }}>
                    NICEIC Enrolment Number: 605485000
                  </p>
                </div>
              </div>
            </div>
            
            
            {/* Footer */}
            
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                columnGap: "8px",
                borderTop: "1px solid #c0c0c0",
                paddingTop: "15px",
                marginTop: "40px",
              }}
            >
              <img
                src={
                  imageBase64["footer"] ||
                  "https://api.test.frontforcecrm.com/logo/icos.png"
                }
                alt="Footer Logo"
                style={{ objectFit: "contain",width:600 }}
              />
            </div>
          </div>
          {isDownloading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "16px",
              }}
            >
              <Spinner color="#ed7d32" loading={isDownloading} size="lg" />
              <p style={{ marginTop: "16px", color: "#4b5563" }}>
                Generating PDF...
              </p>
            </div>
          ) : (
            <button
              onClick={downloadPDF}
              style={{
                marginTop: "16px",
                backgroundColor: "#ed7d32",
                color: "white",
                padding: "8px 16px",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#d66c2b")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#ed7d32")}
            >
              Download PDF
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ReportPDF;